import { Card, Heading, Icon } from '@boltenergy-be/design-system'
import { formatAmount } from 'utils/format.ts'
import Link from 'components/link/Link.tsx'
import { routes } from 'types/routes.ts'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import styles from './UsageCard.module.scss'
import { UsageCardProps, UsageConfig } from './types.ts'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'utils/tracking.ts'
import { EDIT_METER_READINGS_URL_PARMS } from 'pages/app/consumption/edit-meter-readings/constants.ts'

const UsageCard = ({ title, electricity, injection, gas, canAddMeterReadings, isLoading, description }: UsageCardProps) => {
  // i18n
  const { t } = useTranslation()

  // Config
  const USAGE_CONFIG: UsageConfig = {
    electricity: {
      color: '--electric-green',
      icon: 'electricity',
      label: t(($) => $.electricity),
      value: electricity
    },
    injection: {
      color: '--sunny-yellow',
      icon: 'solarPanel',
      label: t(($) => $.injection),
      value: injection
    },
    gas: {
      color: '--purple-haze',
      icon: 'gas',
      label: t(($) => $.gas),
      value: gas
    }
  }

  return (
    <Card as="section" className={styles.card}>
      <header className="flex-col gap-100">
        <Heading as="h2" variant="h5" weight="semi-bold">
          {title}
        </Heading>

        {!!description && <p className="text-sm">{description}</p>}
      </header>

      {isLoading ? (
        <LoadingSkeleton>
          <LoadingSkeleton.Rectangle height={56} />
        </LoadingSkeleton>
      ) : (
        <dl>
          {Object.values(USAGE_CONFIG).map(
            ({ color, icon, label, value }) =>
              typeof value === 'number' && (
                <div key={label} className={styles.entry}>
                  <Icon name={icon} style={{ color: `var(${color})` }} />
                  <dt>{label}</dt>
                  <dd>
                    <data value={value}>{formatAmount(value)} kWh</data>
                  </dd>
                </div>
              )
          )}
        </dl>
      )}

      {canAddMeterReadings && (
        <Link
          href={`${routes.CONSUMPTION_EDIT_METER_READINGS}?${EDIT_METER_READINGS_URL_PARMS.skipConfirmationView}`}
          loading={isLoading}
          representation="button"
          variant="secondary"
          isFullwidth
          className={styles['meter-readings']}
          onNavigate={() =>
            trackEvent({ category: 'meter_readings', object: 'edit', action: 'start' }, { from: routes.BILLING_CYCLE_DETAIL })
          }
        >
          {t(($) => $.billingCycle.usage.addMeterReadings, {
            ns: 'billing'
          })}
        </Link>
      )}
    </Card>
  )
}

export default UsageCard
